<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="mb-4 detail-page-header">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 detail-page-title-row">
          <h3 class="mb-0 detail-page-title">{{ inventory.product ? inventory.product.label : $t('inventory_details') }}</h3>
          <div class="d-flex gap-2">
            <!-- Action buttons can be added here if needed, similar to ProductDetails.vue -->
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Inventory Info -->
      <div v-else-if="inventory && inventory.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'" style="cursor: pointer">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_quotes') != -1">
            <a class="nav-link" :class="{active: step === 'quote'}" @click="step = 'quote'" style="cursor: pointer">{{$t('quote')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_deliveries') != -1">
            <a class="nav-link" :class="{active: step === 'deliveries'}" @click="step = 'deliveries'" style="cursor: pointer">{{$t('deliveries')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_invoices') != -1">
            <a class="nav-link" :class="{active: step === 'invoices'}" @click="step = 'invoices'" style="cursor: pointer">{{$t('invoices')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row">
            <!-- Left Column: Inventory Details -->
            <div class="col-md-8">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />{{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('product_reference')}}</small>
                      <strong>{{inventory.product ? inventory.product.reference : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('label')}}</small>
                      <strong>{{inventory.product ? inventory.product.label : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('unity')}}</small>
                      <strong>{{inventory.product ? inventory.product.unity : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('inventory_reference')}}</small>
                      <strong>{{inventory.reference}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('quantity')}}</small>
                      <strong :class="{'text-danger': inventory.quantity <= inventory.min_quantity}">{{inventory.quantity}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('min_quantity')}}</small>
                      <strong>{{inventory.min_quantity}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('price')}}</small>
                      <strong>{{formatMoney(inventory.price)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('purchase_price')}}</small>
                      <strong>{{formatMoney(inventory.purchase_price)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_by')}}</small>
                      <strong>{{ inventory.user ? inventory.user.name : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_at')}}</small>
                      <strong>{{formatDate(inventory.created_at)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('updated_at')}}</small>
                      <strong>{{formatDate(inventory.updated_at)}}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column: History -->
            <div class="col-md-4">
               <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-clock-history" class="bi bi-clock-history me-2" />{{$t('inventories_history')}}</h6>
                </div>
                <div class="card-body">
                  <inventory-history v-if="inventory && inventory.id" :inventory_id="inventory.id"></inventory-history>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'quote' && helper.checkAccess('view_quotes') != -1">
          <quote-component v-if="inventory && inventory.id" :inventory_id="this.inventory.id"></quote-component>
        </div>

        <div v-if="step === 'deliveries' && helper.checkAccess('view_deliveries') != -1">
          <delivery-component v-if="inventory && inventory.id" :inventory_id="this.inventory.id"></delivery-component>
        </div>

        <div v-if="step === 'invoices' && helper.checkAccess('view_invoices') != -1">
          <invoice-component v-if="inventory && inventory.id" :inventory_id="this.inventory.id"></invoice-component>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import helper from '../../helper'
import quoteComponent from '../../components/QuoteComponent.vue'
import InvoiceComponent from '../../components/InvoiceComponent.vue'
import DeliveryComponent from '../../components/DeliveryComponent.vue'
import InventoryHistory from '../../components/InventoryHistoryComponent.vue'

export default {
  name: 'InventoryDetails',
  components :{
    InvoiceComponent,
    DeliveryComponent,
    quoteComponent,
    InventoryHistory
  },
  data(){
    return {
        inventory : {},
        loading: false,
        step: 'overview'
    }
  },
  mounted(){
    window.mixpanel.track("inventory_details");
    //check permissions
    if(helper.checkAccess('view_inventories') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_inventories') == -2) return this.$router.push({path : '/subscribe'})
  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/inventories')
    this.getInventory(this.$route.params.id)
  },
    methods : {
        async getInventory(id){
            try{
                this.loading = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/inventories/${id}`)
                this.inventory = response.data
                this.loading = false
                
            }catch(error){
                this.$router.push({path : '/inventories'})
                this.loading = false
            }
        },
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { 
            if(!number) return;
            return helper.formatMoney(number);
        }
    },
    computed : {
        helper(){ return helper},
    }
}
</script>
